import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import "./CreateShowcase.scss";

import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../Alerts/Toast";
import { getAccessToken } from "../../../awsAuth";

/* icons */
import addImageIcon from "../img/add-image.png";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

function safeStr(v) {
  return String(v ?? "");
}

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function readTextSafe(res) {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

function extractErrMessage(maybeJsonOrText, fallback) {
  if (!maybeJsonOrText) return fallback;
  if (typeof maybeJsonOrText === "string") return maybeJsonOrText || fallback;
  if (typeof maybeJsonOrText === "object") {
    const msg =
      maybeJsonOrText?.error ||
      maybeJsonOrText?.message ||
      maybeJsonOrText?.details?.message;
    return msg ? String(msg) : fallback;
  }
  return fallback;
}

async function buildAuthHeaders() {
  const raw = await Promise.resolve(getAccessToken());
  const token = raw ? String(raw).trim() : "";
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export default function CreateShowcase({ busy, onClose, onCreate }) {
  const { userId: currentUserId } = useAuth();
  const toast = useToast();

  const type = "services";

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [existingTypes, setExistingTypes] = useState(() => new Set());
  const [loadExistingErr, setLoadExistingErr] = useState("");

  const [isLeaving, setIsLeaving] = useState(false);

  const inputRef = useRef(null);
  const leaveTimerRef = useRef(null);
  const fileRef = useRef(null);

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");

  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  const inFlightRef = useRef(false);

  const uiBusy = !!busy || creating;

  useEffect(() => {
    setTitle("");
    setDescription("");
    setLoadExistingErr("");
    setErr("");
    setIsLeaving(false);

    setCoverFile(null);
    setCoverPreview("");

    inFlightRef.current = false;

    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    const t = setTimeout(() => inputRef.current?.focus?.(), 30);

    return () => {
      clearTimeout(t);
      document.documentElement.style.overflow = prevHtmlOverflow || "";
      document.body.style.overflow = prevBodyOverflow || "";
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) return;

    let alive = true;
    (async () => {
      try {
        setLoadExistingErr("");
        const r = await fetch(`${API_BASE}/api/showcases/user/${encodeURIComponent(currentUserId)}`, {
          cache: "no-store",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        if (!r.ok) throw new Error("load failed");

        const data = await r.json().catch(() => []);
        const items = Array.isArray(data) ? data : [];

        const s = new Set();
        for (const it of items) {
          if (it?.type) s.add(String(it.type));
        }

        if (alive) setExistingTypes(s);
      } catch {
        if (alive) {
          setLoadExistingErr("Не вдалося перевірити існуючі вітрини");
          toast.error("Не вдалося перевірити існуючі вітрини", 2600);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentUserId, toast]);

  const typeAlreadyExists = useMemo(() => {
    return existingTypes && existingTypes.has(type);
  }, [existingTypes, type]);

  const startLeave = useCallback(() => {
    if (isLeaving || uiBusy) return;
    setIsLeaving(true);
    clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => onClose?.(), 220);
  }, [isLeaving, uiBusy, onClose]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && startLeave();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [startLeave]);

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) startLeave();
  };

  const canCreate = useMemo(() => {
    if (!currentUserId) return false;
    if (typeAlreadyExists) return false;
    if (!safeStr(title).trim()) return false;
    if (!safeStr(description).trim()) return false;
    if (!coverFile) return false;
    return true;
  }, [currentUserId, title, description, typeAlreadyExists, coverFile]);

  useEffect(() => {
    return () => {
      if (coverPreview) URL.revokeObjectURL(coverPreview);
    };
  }, [coverPreview]);

  const openFile = () => {
    if (uiBusy || typeAlreadyExists) return;
    fileRef.current?.click?.();
  };

  const onPickCover = (e) => {
    const f = e.target?.files?.[0] || null;
    if (!f) return;

    const okType = new Set(["image/jpeg", "image/png", "image/webp", "image/avif"]);
    if (!okType.has(f.type)) {
      e.target.value = "";
      setErr("Непідтримуваний тип файлу (jpeg/png/webp/avif)");
      toast.error("Непідтримуваний тип файлу (jpeg/png/webp/avif)", 3200);
      return;
    }

    if (coverPreview) URL.revokeObjectURL(coverPreview);

    setErr("");
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
    e.target.value = "";
  };

  const clearCover = () => {
    if (uiBusy) return;
    if (coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverPreview("");
    setCoverFile(null);
  };

  const createShowcase = useCallback(async () => {
    if (inFlightRef.current) return;

    if (uiBusy) return;
    if (!canCreate) return;

    inFlightRef.current = true;
    setCreating(true);
    setErr("");

    try {
      if (!currentUserId) throw new Error("Немає userId (авторизація/контекст)");
      if (!coverFile) throw new Error("Обкладинка обов'язкова");

      const payload = {
        type,
        title: safeStr(title).trim(),
        description: safeStr(description).trim(),
        status: "draft",
        coverUrl: "",
        blocks: [],
      };

      const authHeaders = await buildAuthHeaders();
      console.log("[CreateShowcase] authHeaders =", authHeaders);

      const resCreate = await fetch(`${API_BASE}/api/showcases`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      let createdItem = null;

      if (!resCreate.ok) {
        if (resCreate.status === 409) {
          const j = await readJsonSafe(resCreate);
          const txt = j ? "" : await readTextSafe(resCreate);

          const existing = j?.item || j?.data || j;
          const existingId = safeStr(existing?.id).trim();

          if (existingId) {
            setExistingTypes((prev) => {
              const next = new Set(prev || []);
              next.add(type);
              return next;
            });
            createdItem = existing;
          } else {
            throw new Error(extractErrMessage(j || txt, "Вітрина цього типу вже існує"));
          }
        } else {
          const j = await readJsonSafe(resCreate);
          const txt = j ? "" : await readTextSafe(resCreate);
          throw new Error(extractErrMessage(j || txt, `Помилка створення (${resCreate.status})`));
        }
      } else {
        const createdJson = await readJsonSafe(resCreate);
        createdItem = createdJson?.item || createdJson?.data || createdJson;
      }

      const showcaseId = safeStr(createdItem?.id).trim();
      if (!showcaseId) {
        throw new Error("Не отримали id вітрини після створення");
      }

      const fd = new FormData();
      fd.append("cover", coverFile);

      const coverAuthHeaders = await buildAuthHeaders();
      console.log("[CreateShowcase] coverAuthHeaders =", coverAuthHeaders);

      const resCover = await fetch(`${API_BASE}/api/showcases/${encodeURIComponent(showcaseId)}/cover`, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: {
          ...coverAuthHeaders,
        },
      });

      if (!resCover.ok) {
        const j = await readJsonSafe(resCover);
        const txt = j ? "" : await readTextSafe(resCover);
        throw new Error(extractErrMessage(j || txt, `Помилка завантаження обкладинки (${resCover.status})`));
      }

      const coverJson = await readJsonSafe(resCover);
      const finalItem = coverJson?.item || coverJson?.data || createdItem;
      const finalCoverUrl = safeStr(coverJson?.coverUrl || finalItem?.coverUrl || "");

      const out = {
        ...createdItem,
        ...(finalItem && typeof finalItem === "object" ? finalItem : {}),
        ...(finalCoverUrl ? { coverUrl: finalCoverUrl } : {}),
      };

      try {
        onCreate?.(out);
      } catch (cbErr) {
        console.error("[CreateShowcase] onCreate callback error=", cbErr);
      }

      toast.success("Вітрину створено", 2200);

      try {
        startLeave();
      } catch (leaveErr) {
        console.error("[CreateShowcase] startLeave error=", leaveErr);
      }
    } catch (e) {
      const msg = e?.message || "Не вдалося створити вітрину";
      setErr(msg);
      toast.error(msg, 3200);
    } finally {
      setCreating(false);
      inFlightRef.current = false;
    }
  }, [uiBusy, canCreate, currentUserId, coverFile, type, title, description, onCreate, startLeave, toast]);

  const submit = () => {
    createShowcase();
  };

  return (
    <div
      className={[
        "create-showcase-page",
        isLeaving ? "is-leaving" : "",
      ].join(" ")}
      onClick={onBackdropClick}
      aria-modal="true"
      role="dialog"
      aria-label="Створення вітрини"
    >
      <div className="csh-backdrop" />

      <div className="csh-shell" onClick={(e) => e.stopPropagation()}>
        <div className="csh-card">
          <div className="csh-top">
            <div className="csh-title">Створити вітрину</div>
            <button type="button" className="csh-close" onClick={startLeave} disabled={uiBusy}>
              <span className="csh-closeIcon" />
            </button>
          </div>

          {false && !!loadExistingErr && <div className="csh-warn">{loadExistingErr}</div>}
          {false && !!err && <div className="csh-warn">{err}</div>}

          {typeAlreadyExists && (
            <div className="csh-info">
              У тебе вже є вітрина цього типу. На цьому етапі — <b>1 тип = 1 вітрина</b>.
            </div>
          )}

          {!currentUserId ? (
            <div className="csh-info">
              Потрібна авторизація, щоб створити вітрину.
            </div>
          ) : null}

          <div className="csh-coverWrap">
            <div className="csh-coverHead">
              <div className="csh-coverLabel">Обкладинка *</div>

              <div className="csh-coverActions">
                {!!coverFile && (
                  <button
                    type="button"
                    className="csh-miniBtn csh-miniBtn--ghost"
                    onClick={openFile}
                    disabled={uiBusy || typeAlreadyExists}
                  >
                    Змінити
                  </button>
                )}

                {!!coverFile && (
                  <button
                    type="button"
                    className="csh-miniBtn csh-miniBtn--danger"
                    onClick={clearCover}
                    disabled={uiBusy || typeAlreadyExists}
                  >
                    Прибрати
                  </button>
                )}
              </div>
            </div>

            <div
              className={["csh-cover", coverPreview ? "has-cover" : ""].join(" ")}
              role="button"
              tabIndex={0}
              onClick={openFile}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") openFile();
              }}
              aria-label="Обрати обкладинку"
            >
              {coverPreview ? (
                <img className="csh-coverImg" src={coverPreview} alt="Обкладинка" />
              ) : (
                <div className="csh-coverPlaceholder">
                  <img className="csh-coverAddIcon" src={addImageIcon} alt="" />
                  <div className="csh-coverTitle">Оберіть обкладинку</div>
                </div>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/avif"
                onChange={onPickCover}
                className="csh-file"
                aria-hidden="true"
                tabIndex={-1}
              />
            </div>

            <div className="csh-coverHint">Формат: подовгастий банер (як у прикладі)</div>
          </div>

          <div className="csh-field">
            <label className="csh-label" htmlFor="csh-title">
              Назва вітрини *
            </label>
            <input
              id="csh-title"
              ref={inputRef}
              className="csh-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Наприклад: Мої послуги"
              maxLength={80}
              disabled={uiBusy || typeAlreadyExists}
              autoComplete="off"
              inputMode="text"
              enterKeyHint="next"
            />
          </div>

          <div className="csh-field">
            <label className="csh-label" htmlFor="csh-desc">
              Короткий опис (1–2 речення) *
            </label>
            <textarea
              id="csh-desc"
              className="csh-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Хто ти і що тут буде"
              maxLength={200}
              disabled={uiBusy || typeAlreadyExists}
            />
          </div>

          <div className="csh-actions">
            <button type="button" className="csh-btn ghost" onClick={startLeave} disabled={uiBusy}>
              Скасувати
            </button>

            <button
              type="button"
              className="csh-btn primary"
              onClick={submit}
              disabled={uiBusy || !canCreate}
              title={
                typeAlreadyExists
                  ? "Вітрина вже існує"
                  : !currentUserId
                  ? "Потрібна авторизація"
                  : !coverFile
                  ? "Спочатку обери обкладинку"
                  : ""
              }
            >
              {uiBusy ? "Створюємо…" : "Створити"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}